import React, { useState, useEffect } from 'react';
import KpiChart from '../components/Dashboard/KpiChart';
import CompetitiveKPIDashboard from '../components/Dashboard/CompetitiveKPIDashboard';
import { Grid, Box, Card, CardContent, useTheme, CircularProgress, Typography, Fade } from '@mui/material';
import { useDomain, DOMAINS } from '../contexts/DomainContext';
import IotChart from "../components/Charts/IotChart";
import PowerHeatmapChart from "../components/Charts/PowerHeatmapChart";
import { useGraphQL } from '../apollo/useGraphQL';
import { GET_DASHBOARD_DATA, GET_KPI_CHART_DATA } from '../graphql-queries/KPI/queries';
import { enhanceKpiCollectionWithForecast } from '../utils/forecast.js';

// Dashboard 컨테이너 컴포넌트
const DashboardContainer = () => {
  // 통합 대시보드 데이터 상태 관리
  const [dashboardData, setDashboardData] = useState({
    kpiData: [],
    performanceData: null,
    heatmapData: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardVisible, setDashboardVisible] = useState(false);
  
  // 오늘 날짜를 YYYY-MM-DD 형식으로 가져오기 (로컬 시간대 기준)
  const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // KPI 지표별 필터 상태 (categoryCd + kpiIndicatorCd 조합으로 구분)
  const [indicatorFilters, setIndicatorFilters] = useState({});
  
  // 현재 테마 가져오기
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';
  const { executeQuery } = useGraphQL();
  
  
  // 도메인별 배경색 가져오기
  const getCardBgColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#2d1e0f' : '#ffffff';
    }
    return isDarkMode ? '#102a43' : '#ffffff';
  };
  
  // 도메인별 테두리 색상 가져오기
  const getBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#f5e8d7';
    }
    return isDarkMode ? '#2d4764' : '#e0e0e0';
  };

  // 통합 대시보드 데이터 로드
  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // 지표별 필터가 있는지 확인
      const hasFilters = Object.keys(indicatorFilters).length > 0;

      const defaultDate = getTodayFormatted();
      const defaultRange = 'week';

      let filterPayload = {
        date: defaultDate,
        range: defaultRange
      };

      if (hasFilters) {
        const [, firstFilterValue] = Object.entries(indicatorFilters)[0] || [];

        if (firstFilterValue) {
          filterPayload = {
            date: firstFilterValue.date || defaultDate,
            range: firstFilterValue.range || defaultRange,
            ...(firstFilterValue.site ? { site: firstFilterValue.site } : {}),
            ...(firstFilterValue.compCd ? { compCd: firstFilterValue.compCd } : {})
          };
        }
      }

      // 통합 GraphQL 쿼리 실행
      const result = await executeQuery(GET_DASHBOARD_DATA, {
        filter: filterPayload
      });

      if (result?.data?.getDashboardData) {
        const data = result.data.getDashboardData;
        let effectiveFilters = indicatorFilters;

        // 새로 받은 데이터를 기반으로 필터 상태 업데이트 (초기 로드 시에만)
        if (!hasFilters && data.kpiData && data.kpiData.length > 0) {
          const defaultDate = getTodayFormatted();
          const defaultRange = 'week';

          const initialFilters = {};
          data.kpiData.forEach(item => {
            const key = `${item.categoryCd}|${item.kpiIndicatorCd}`;
            initialFilters[key] = {
              categoryCd: item.categoryCd,
              date: defaultDate,
              range: defaultRange
            };
          });

          effectiveFilters = initialFilters;
          setIndicatorFilters(initialFilters);
        }

        // 8PIN 조건이 아닌 경우 예측치 처리를 하지 않음
        const enhancedKpiData = data.kpiData && data.kpiData.some(item =>
          item.kpiTitle?.includes('에잇핀') ||
          (item.chartData && JSON.parse(typeof item.chartData === 'string' ? item.chartData : JSON.stringify(item.chartData))[0] &&
           Object.keys(JSON.parse(typeof item.chartData === 'string' ? item.chartData : JSON.stringify(item.chartData))[0]).some(key => key?.includes('에잇핀')))
        ) ? enhanceKpiCollectionWithForecast(data.kpiData, effectiveFilters) : data.kpiData || [];

        // 통합 데이터 설정
        setDashboardData({
          kpiData: enhancedKpiData,
          performanceData: data.performanceData,
          heatmapData: data.heatmapData
        });
      } else {
        // 데이터 없음
        setDashboardData({
          kpiData: [],
          performanceData: null,
          heatmapData: null
        });
      }
    } catch (error) {
      console.error('대시보드 데이터 로드 중 오류 발생:', error);
      setDashboardData({
        kpiData: [],
        performanceData: null,
        heatmapData: null
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 단일 KPI 데이터 로드
  const loadSingleKpiData = async (kpiIndicatorCd, filter) => {
    try {
      const key = `${filter.categoryCd}|${kpiIndicatorCd}`;
      
      // 현재 필터 상태 업데이트
      const updatedFilters = {
        ...indicatorFilters,
        [key]: filter
      };
      
      // 변경된 지표의 필터만 전송
      const filters = [{
        kpiIndicatorCd,
        categoryCd: filter.categoryCd,
        date: filter.date,
        range: filter.range
      }];
      
      // GraphQL 쿼리 실행
      const result = await executeQuery(GET_KPI_CHART_DATA, {
        filters
      });
      
      if (result?.data?.getKpiChartData) {
        // 새 데이터로 해당 차트만 업데이트
        const newChartData = result.data.getKpiChartData;
        // 8PIN 조건이 아닌 경우 예측치 처리를 하지 않음
        const enhancedChartData = newChartData.some(item =>
          item.kpiTitle?.includes('에잇핀') ||
          (item.chartData && JSON.parse(typeof item.chartData === 'string' ? item.chartData : JSON.stringify(item.chartData))[0] &&
           Object.keys(JSON.parse(typeof item.chartData === 'string' ? item.chartData : JSON.stringify(item.chartData))[0]).some(key => key?.includes('에잇핀')))
        ) ? enhanceKpiCollectionWithForecast(newChartData, { [key]: filter }) : newChartData;
        
        setDashboardData(prevData => {
          // 해당 차트 데이터만 업데이트하고 나머지는 유지
          if (enhancedChartData.length === 0) return prevData;

          return {
            ...prevData,
            kpiData: prevData.kpiData.map(item => {
              const updatedItem = enhancedChartData.find(newItem =>
                newItem.kpiIndicatorCd === item.kpiIndicatorCd &&
                newItem.categoryCd === item.categoryCd
              );
              return updatedItem || item;
            })
          };
        });
        
        // 필터 상태 업데이트
        setIndicatorFilters(updatedFilters);
      }
    } catch (error) {
      console.error(`KPI 데이터 로드 중 오류 발생 (${kpiIndicatorCd}):`, error);
    }
  };

  // 처음 로드 시 통합 대시보드 데이터 가져오기
  useEffect(() => {
    const initializeDashboard = async () => {
      await loadDashboardData();
    };
    initializeDashboard();
    // 대시보드 페이드인 애니메이션
    setTimeout(() => setDashboardVisible(true), 300);
  }, []);

  // 개별 차트 필터 변경 핸들러
  const handleChartFilterChange = (kpiIndicatorCd, newFilter, refresh = false) => {
    const key = `${newFilter.categoryCd}|${kpiIndicatorCd}`;
    
    // 필터 상태 업데이트
    setIndicatorFilters(prev => ({
      ...prev,
      [key]: newFilter
    }));
    
    // 필터 변경 즉시 데이터 새로고침 요청이 있으면 로드
    if (refresh) {
      loadSingleKpiData(kpiIndicatorCd, newFilter);
    }
  };

  // KPI 차트 렌더링
  const renderKpiCharts = () => {
    if (isLoading) {
      return (
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            boxShadow: 3,
            bgcolor: getCardBgColor(),
            border: `1px solid ${getBorderColor()}`,
            height: '100%',
            minHeight: '200px'
          }}>
            <CardContent sx={{ 
              height: '100%', 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              pb: 3,
              '&:last-child': { pb: 3 }
            }}>
              <CircularProgress />
            </CardContent>
          </Card>
        </Grid>
      );
    }
    
    if (!dashboardData.kpiData || dashboardData.kpiData.length === 0) {
      return (
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            boxShadow: 3,
            bgcolor: getCardBgColor(),
            border: `1px solid ${getBorderColor()}`,
            height: '100%',
            minHeight: '200px'
          }}>
            <CardContent sx={{ 
              height: '100%', 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              pb: 3,
              '&:last-child': { pb: 3 }
            }}>
              <Typography variant="h6" color="text.secondary" component="div">
                구독 중인 KPI 데이터가 없습니다.
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                KPI 설정에서 관심 있는 지표를 구독해주세요.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      );
    }
    
    // 구독한 KPI 차트들을 표시
    return dashboardData.kpiData.map((data, index) => {
      const key = `${data.categoryCd}|${data.kpiIndicatorCd}`;
      // 카드 개수에 따라 반응형 크기 계산
      const getResponsiveSize = () => {
        const cardCount = dashboardData.kpiData.length;
        if (cardCount === 1) return 12; // 1개면 전체 너비
        if (cardCount === 2) return 6;  // 2개면 50%씤
        if (cardCount === 3) return 4;  // 3개면 33.33%씩
        return 3; // 4개 이상이면 25%씩
      };
      
      return (
        <Grid item xs={12} md={getResponsiveSize()} key={`kpi-chart-${key || index}`} sx={{ mb: { xs: 2, md: 0 } }}>
          <Card sx={{ 
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            bgcolor: '#ffffff',
            border: `1px solid #f0f0f0`,
            height: '100%',
            borderRadius: 3,
            background: '#ffffff',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
            }
          }}>
            <CardContent sx={{ 
              height: '100%', 
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              pb: 3,
              '&:last-child': { pb: 3 }
            }}>
              <KpiChart
                kpiData={data}
                isLoading={false}
                onFilterChange={handleChartFilterChange}
                filter={indicatorFilters[key] || {
                  categoryCd: data.categoryCd,
                  date: getTodayFormatted(),
                  range: 'week'
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      );
    });
  };

  return (
    <Fade in={dashboardVisible} timeout={800}>
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        p: 0.5,
        overflow: 'auto',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDarkMode
            ? 'radial-gradient(circle at 20% 50%, rgba(179, 197, 230, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(240, 230, 217, 0.05) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(33, 150, 243, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: -1
        }
      }}>
        {/* 최상단: KPI 성과 대시보드 + 전력 히트맵 */}
        <Box sx={{
          flex: '0 1 26%',
          minHeight: '180px',
          maxHeight: '350px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Grid container spacing={1} sx={{ flex: 1, minHeight: 0 }}>
            <Grid item xs={12} md={8} sx={{ display: 'flex', height: '100%' }}>
              <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
                <CompetitiveKPIDashboard
                  kpiData={dashboardData.kpiData}
                  performanceData={dashboardData.performanceData}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', height: '100%' }}>
              <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
                <PowerHeatmapChart
                  heatmapData={dashboardData.heatmapData}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* 중간: KPI 차트들 (가동률, 생산량 등) */}
        <Box sx={{
          flex: '0 1 40%',
          minHeight: '250px',
          maxHeight: '500px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Grid container spacing={1} sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'visible',
            '& .MuiGrid-item': {
              overflow: 'visible',
              height: '100%'
            }
          }}>
            {renderKpiCharts()}
          </Grid>
        </Box>

        {/* 하단: IOT 전력량 차트 */}
        <Box sx={{
          flex: '0 1 26%',
          minHeight: '180px',
          maxHeight: '350px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Card sx={{
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            bgcolor: '#ffffff',
            border: `1px solid #f0f0f0`,
            borderRadius: 3,
            flex: 1,
            minHeight: 0,
            background: '#ffffff',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
            }
          }}>
            <CardContent sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              '&:last-child': { pb: 0 }
            }}>
              <IotChart />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Fade>
  );
};

export default DashboardContainer;
